import { DataUtilities } from "../Common/DataUtilities";
import { StateManager } from "../Common/StateManager";
import { ValidationIssue } from "../Common/IDesignValidationProvider";
import { ExecutionData } from "../Common/Data/ExecutionData/ExecutionData";
import { IConfigStorage } from "../Common/IConfigStorage";
import { NumberDataDefinition } from "../Common/Data/Definitions/NumberDataDefinition";
import { IDataDefinition } from "../Common/Data/Definitions/IDataDefinition";
import { DataTypes } from "../Common/Data/DataTypes";
import { DataDefinitionUtilities } from "../Common/DataDefinitionUtilities";
import { ITestingContext } from "../Common/Testing/ITestingContext";
import { EventLog, EventLogType } from "../Common/Event/EventLog";
import { IBaseExpressionElement } from "./IBaseExpressionElement";
import { ExpressionValueType, ObjectExpressionValueType, StringExpressionValueType } from "./ExpressionValueType";
import { ExpressionContext } from "./ExpressionContext";
import { ExpressionRule } from "./ExpressionRule";
import { ExpressionConstants } from "./ExpressionContants";
import { ExpressionValueTestResult } from "./ExpressionTestResult";
import { GetExpressionRuleFromData } from "./RegisterExpressionRule";
import { Executable } from "../Common/Execution/Executable";
import { RuleExecutable } from "../Common/Execution/RuleExecutable";
import { DataDefinition } from "../Common/Data/DataDefinition";
import type { AppGlueAny } from "../Common/AppGlueAny";

export class ExpressionValue implements IBaseExpressionElement, IConfigStorage {
    //
    // --- begin prototypes for creation of mapping and merge in storage.
    // cannot directly use classes as it produces a reference loop
    //
    static mergePrototype: AppGlueAny;
    static mappingPrototype: AppGlueAny;
    //
    // --- end merge and mapping integration
    //
    readonly canSelect: boolean = true;
    _id: string = DataUtilities.generateUniqueId();
    name?: string;
    editContext?: ExpressionContext;
    mappingData: Executable | undefined;
    stringMergeData: RuleExecutable | undefined;
    configDescription: string | undefined;
    private expectedTypeValue: IDataDefinition = new NumberDataDefinition();
    private valueTypeValue: ExpressionValueType = ExpressionValueType.UNSET;
    private subExpressionValue?: ExpressionRule;
    private expressionValue: AppGlueAny;
    private variableNameValue?: string;

    private _stringValueType: StringExpressionValueType = StringExpressionValueType.SHORT;

    get stringValueType(): StringExpressionValueType {
        return this._stringValueType;
    }

    set stringValueType(value: StringExpressionValueType) {
        if (
            this.expectedTypeValue &&
            this.expectedTypeValue.type !== DataTypes.STRING &&
            value !== StringExpressionValueType.SHORT
        )
            throw new Error("string value type can only be set on string expression values");

        if (this.expectedTypeValue && this.expectedTypeValue.list === true && value !== StringExpressionValueType.SHORT)
            throw new Error("string value type can only be set on string expression values that are not lists");

        this._stringValueType = value;

        if (value === StringExpressionValueType.MERGE && !this.stringMergeData) {
            if (!ExpressionValue.mergePrototype)
                throw new Error("Run AppGlueCommonInit.init() before using this class");
            // @ts-ignore
            let val = new ExpressionValue.mergePrototype.constructor();
            // @ts-ignore
            this.stringMergeData = val as RuleExecutable;
        }

        if (this.editContext && this.stringMergeData) {
            this.stringMergeData.inputData = () => {
                return this.editContext!.expression.getInputDefinition();
            };
        }
    }

    private _objectValueType: ObjectExpressionValueType = ObjectExpressionValueType.CONSTANT;

    get objectValueType(): ObjectExpressionValueType {
        return this._objectValueType;
    }

    set objectValueType(value: ObjectExpressionValueType) {
        if (
            this.expectedTypeValue &&
            this.expectedTypeValue.type !== DataTypes.OBJECT &&
            value !== ObjectExpressionValueType.CONSTANT
        )
            throw new Error("object value type can only be set on string expression values");

        if (
            this.expectedTypeValue &&
            this.expectedTypeValue.list === true &&
            value !== ObjectExpressionValueType.CONSTANT
        )
            throw new Error("object value type can only be set on object expression values that are not lists");

        this._objectValueType = value;

        if (value === ObjectExpressionValueType.MAPPED && !this.mappingData) {
            if (!ExpressionValue.mappingPrototype)
                throw new Error("Run AppGlueCommonInit.init() before using this class");
            // @ts-ignore
            let val = new ExpressionValue.mappingPrototype.constructor();
            // @ts-ignore
            this.mappingData = val as Executable;
        }

        this.setupMappingResult();

        if (this.editContext && this.mappingData) {
            this.mappingData.inputData = () => {
                return this.editContext!.expression.getInputDefinition();
            };
        }
    }

    get variableName(): string | undefined {
        return this.variableNameValue;
    }

    set variableName(value: string | undefined) {
        this.variableNameValue = value;

        if (value === undefined) this.valueType = ExpressionValueType.UNSET;
        else this.valueType = ExpressionValueType.VARIABLE;

        if (this.editContext) this.editContext.refresh();
    }

    get value(): AppGlueAny {
        return this.expressionValue;
    }

    set value(value: AppGlueAny) {
        this.expressionValue = value;

        if (value === undefined) this.valueType = ExpressionValueType.UNSET;
        else this.valueType = ExpressionValueType.VALUE;

        if (this.editContext) this.editContext.refresh();
    }

    get subExpression(): ExpressionRule | undefined {
        return this.subExpressionValue;
    }

    set subExpression(value: ExpressionRule | undefined) {
        this.subExpressionValue = value;

        if (!value) {
            this.valueType = ExpressionValueType.UNSET;
        } else {
            if (value.__type === ExpressionConstants.IfThenExpressionType) {
                value.expressionValueType = this.expectedType;
            }

            this.valueType = ExpressionValueType.SUBEXPRESSION;
            // this.expectedTypeValue = value.expressionValueType;
            if (this.editContext) {
                value.setExpressionContext(this.editContext, this);
            }
        }

        if (this.editContext) this.editContext.refresh();
    }

    get valueType(): ExpressionValueType {
        return this.valueTypeValue;
    }

    set valueType(value: ExpressionValueType) {
        this.valueTypeValue = value;
        StateManager.propertyChanged(this, "valueTypeValue");

        if (this.editContext) this.editContext.refresh();
    }

    get expectedType(): IDataDefinition {
        return this.expectedTypeValue;
    }

    set expectedType(value: IDataDefinition) {
        this.expectedTypeValue = value;
        if (this.editContext) this.editContext.refresh();
    }

    static createExpressionValue(
        owner: ExpressionRule,
        name: string,
        expectedType: IDataDefinition = new NumberDataDefinition(),
    ): ExpressionValue {
        let v = new ExpressionValue();
        v.name = name;
        v.expectedTypeValue = expectedType;
        owner.registerExpressionValue(v);
        return v;
    }

    static isExpressionValue(item: AppGlueAny): item is ExpressionValue {
        return item instanceof ExpressionValue;
    }

    setValueWithoutRefresh(value: AppGlueAny) {
        this.expressionValue = value;
    }

    getMergeClone(): RuleExecutable | undefined {
        if (!this.stringMergeData) return;

        if (!ExpressionValue.mergePrototype) throw new Error("Run AppGlueCommonInit.init() before using this class");
        // @ts-ignore
        let val = new ExpressionValue.mergePrototype.constructor();
        // @ts-ignore
        let data = val as RuleExecutable;

        data.setStorageData(this.stringMergeData?.getStorageData());

        if (this.editContext && data) {
            data.inputData = () => {
                return this.editContext!.expression.getInputDefinition();
            };
        }

        return data;
    }

    getMappingClone(): Executable | undefined {
        if (!this.mappingData) return;

        if (!ExpressionValue.mappingPrototype) throw new Error("Run AppGlueCommonInit.init() before using this class");
        // @ts-ignore
        let val = new ExpressionValue.mappingPrototype.constructor();
        // @ts-ignore
        let data = val as Executable;

        data.setStorageData(this.mappingData?.getStorageData());

        this.setupMappingResult(data);

        if (this.editContext && data) {
            data.inputData = () => {
                return this.editContext!.expression.getInputDefinition();
            };
        }

        return data;
    }

    setExpressionContext(context: ExpressionContext, owner: IBaseExpressionElement): void {
        this.editContext = context;
        this.editContext.register(this, owner);

        if (this.subExpression) {
            this.subExpression.setExpressionContext(context, this);
        }
    }

    clear() {
        this.value = undefined;
        this.subExpression = undefined;
        this.variableName = undefined;
        this.valueType = ExpressionValueType.UNSET;
        if (this.editContext?.expression) StateManager.changed(this.editContext.expression);
    }

    getValidationIssues(): ValidationIssue[] {
        // check type
        // check to see if unset

        let issues: ValidationIssue[] = [];

        if (this.valueTypeValue === ExpressionValueType.UNSET)
            issues.push(new ValidationIssue("Expression Value is Required", undefined, this._id));

        if (this.valueTypeValue === ExpressionValueType.VARIABLE) {
            if (!this.variableName) issues.push(new ValidationIssue("Data Name is Required", undefined, this._id));
            else {
                try {
                    const field =
                        this.editContext?.definition?.requiredVariable?.name === this.variableName
                            ? this.editContext?.definition?.requiredVariable
                            : (DataUtilities.getFieldFromDataDefinitionByName(
                                  this.editContext?.definition?.getInputDefinition().fields || [],
                                  this.variableName,
                              ) as IDataDefinition);
                    if (!field) {
                        issues.push(new ValidationIssue("Invalid Data Name.", undefined, this._id));
                    } else if (field) {
                        if (field.type !== this.expectedTypeValue.type || field.list !== this.expectedTypeValue.list) {
                            issues.push(
                                new ValidationIssue(
                                    `${this.expectedTypeValue.type}${
                                        this.expectedTypeValue.list ? " list" : ""
                                    } field should be selected.`,
                                ),
                            );
                        }
                    }
                } catch (e) {
                    issues.push(new ValidationIssue("Invalid Data Name.", undefined, this._id));
                }
            }

            return issues;
        }

        if (this.valueTypeValue === ExpressionValueType.VALUE) {
            if (this.value === null || this.value === undefined)
                issues.push(new ValidationIssue("Value is Required", undefined, this._id));
            else {
                let issue = this.expectedType.verifyValueType(this.value);
                if (issue) {
                    issues.push(issue);
                }
            }
        }

        if (this.valueTypeValue === ExpressionValueType.SUBEXPRESSION) {
            if (!this.subExpression) issues.push(new ValidationIssue("Expression is Required", undefined, this._id));
            else {
                if (
                    this.subExpression.expressionValueType.type !== this.expectedTypeValue.type ||
                    this.subExpression.expressionValueType.list !== this.expectedTypeValue.list
                ) {
                    issues = [
                        ...issues,
                        new ValidationIssue(
                            `${this.expectedTypeValue.type} expression should be used.  Expression returned a ${this.subExpression.expressionValueType.type}.`,
                        ),
                    ];
                }
            }
        }

        return issues;
    }

    evaluate(
        data: ExecutionData,
        testContext: ITestingContext | undefined,
        undefinedAllowed: boolean = false,
    ): AppGlueAny {
        let logs: EventLog[] = [];
        let val: AppGlueAny;
        switch (this.valueTypeValue) {
            case ExpressionValueType.VARIABLE:
                if (this.expectedTypeValue.type === DataTypes.STRING && !this.expectedTypeValue.list)
                    val = DataUtilities.getString(data.values, this.variableName!);
                else val = DataUtilities.get(data.values, this.variableName!);
                logs.push(new EventLog(`Expression Variable: ${this.variableName} = ${val}`));
                break;
            case ExpressionValueType.VALUE:
                val = this.value;
                logs.push(new EventLog(`Expression Constant Value: ${val}`));
                break;
            case ExpressionValueType.SUBEXPRESSION:
                try {
                    logs.push(new EventLog(`Expression Evaluate ${this.subExpression!.__type}`));

                    val = this.subExpression!.evaluate(data, testContext);

                    if (val === undefined) {
                        logs.push(new EventLog(`Expression ${this.subExpression!.__type} Error:`, EventLogType.ERROR));
                    } else {
                        logs.push(new EventLog(`Expression ${this.subExpression!.__type} Result: ${val}`));
                    }
                } catch (e) {
                    if (testContext) {
                        logs.push(
                            new EventLog(
                                `Expression ${this.subExpression!.__type} Error: ${e.message}`,
                                EventLogType.ERROR,
                            ),
                        );
                        testContext.addLogItems(logs);

                        let testresult = new ExpressionValueTestResult(this._id, e.message);
                        testresult.isError = true;
                        testContext.addTestResultItem(testresult);
                    }
                    throw e;
                }

                break;
            default:
                val = undefined;
        }

        // check if undefined is allowed
        if (val === undefined && !undefinedAllowed) {
            if (testContext) {
                if (
                    this.valueTypeValue === ExpressionValueType.SUBEXPRESSION ||
                    this.valueTypeValue === ExpressionValueType.VARIABLE
                ) {
                    let testresult = new ExpressionValueTestResult(
                        this._id,
                        this.valueTypeValue === ExpressionValueType.SUBEXPRESSION
                            ? `Expression Value: "${this.name}" is not defined`
                            : `Expression Value: "${this.variableName}" is missing`,
                    );
                    testresult.isError = true;
                    testContext.addTestResultItem(testresult);

                    logs.push(new EventLog(testresult.result!, EventLogType.ERROR));

                    testContext.addLogItems(logs);

                    throw new Error(testresult.result!);
                }
                throw new Error("Expression Value: Contant Value is not defined");
            } else {
                throw new Error(`Expression Value: ${this.name} is not defined`);
            }
        }

        // do conversion
        val = this.expectedType.convert(val);

        // check type of return
        let valIssue = DataUtilities.compareValueToDataDefinition(val, this.expectedType);

        if (valIssue) {
            if (testContext) {
                let testresult = new ExpressionValueTestResult(
                    this._id,
                    `Expression Value: ${this.variableName || this.name} value returned is not of expected type. ${
                        valIssue.issue
                    }`,
                );
                testresult.isError = true;
                testContext.addTestResultItem(testresult);

                logs.push(
                    new EventLog(
                        `Expression Value: ${this.name} value returned is not of expected type. ${valIssue.issue}`,
                        EventLogType.ERROR,
                    ),
                );

                testContext.addLogItems(logs);

                if (this.valueTypeValue === ExpressionValueType.VARIABLE) {
                    throw new Error(
                        `Expression Value: "${this.variableName}" value returned is not of expected type. ${valIssue.issue}`,
                    );
                }

                throw new Error(
                    `Expression Value: ${this.variableName || this.name} value returned is not of expected type. ${
                        valIssue.issue
                    }`,
                );
            } else {
                throw new Error(
                    `Expression Value: ${this.variableName || this.name} value returned is not of expected type. ${
                        valIssue.issue
                    }`,
                );
            }
        }

        if (testContext) {
            // publish out value as result.
            testContext.addTestResultItem(new ExpressionValueTestResult(this._id, val.toString()));

            testContext.addLogItems(logs);
        }

        return val;
    }

    getAllVariables(): string[] {
        let output: string[] = [];

        if (this.valueTypeValue === ExpressionValueType.VARIABLE && this.variableName) {
            output.push(this.variableName);
        } else if (this.valueTypeValue === ExpressionValueType.SUBEXPRESSION && this.subExpression) {
            if (this.subExpression) {
                let vars = this.subExpression.getAllVariables();
                if (vars) {
                    output.push(...vars);
                }
            }
        }

        return output;
    }

    getStorageData(): object {
        let data = {};

        Reflect.set(data, "_id", this._id);
        Reflect.set(data, "name", this.name);
        Reflect.set(data, "expectedTypeValue", this.expectedTypeValue.getStorageData());
        Reflect.set(data, "valueType", this.valueTypeValue);
        let exp = this.subExpressionValue?.getStorageData();
        if (exp) {
            DataDefinitionUtilities.verifyTypeField(exp);
            Reflect.set(data, "subExpressionValue", exp);
        }
        Reflect.set(data, "expressionValue", this.expressionValue);
        Reflect.set(data, "variableName", this.variableNameValue);

        Reflect.set(data, "stringValueType", this._stringValueType);
        Reflect.set(data, "objectValueType", this._objectValueType);
        if (this.mappingData?.getStorageData()) Reflect.set(data, "mappingValue", this.mappingData?.getStorageData());
        if (this.mappingData?.getStorageData()) Reflect.set(data, "mergeValue", this.stringMergeData?.getStorageData());

        Reflect.set(data, "configDescription", this.configDescription);

        return data;
    }

    setStorageData(data: object): void {
        this._id = Reflect.get(data, "_id");
        this.name = Reflect.get(data, "name");

        let dd = DataDefinitionUtilities.getDataDefinitionFromData(Reflect.get(data, "expectedTypeValue"));

        if (dd) {
            this.expectedTypeValue = dd;
        } else {
            throw new Error("no data definition defined");
        }
        // todo: create the right type

        this.valueTypeValue = Reflect.get(data, "valueType");

        this.subExpressionValue = GetExpressionRuleFromData(Reflect.get(data, "subExpressionValue"));
        if (this.subExpressionValue && this.editContext) {
            this.subExpressionValue.setExpressionContext(this.editContext, this);
        }
        // todo: create the right type

        this.expressionValue = Reflect.get(data, "expressionValue");

        // do date conversion
        if (!dd.list && dd.type === DataTypes.DATE) {
            if (!isNaN(new Date(this.expressionValue).getTime())) {
                // is valid date
                this.expressionValue = new Date(this.expressionValue);
            }
        }

        this.variableNameValue = Reflect.get(data, "variableName");

        this._stringValueType = Reflect.get(data, "stringValueType") ?? StringExpressionValueType.SHORT;
        this._objectValueType = Reflect.get(data, "objectValueType") ?? ObjectExpressionValueType.CONSTANT;

        if (Reflect.get(data, "mappingValue")) {
            if (!ExpressionValue.mappingPrototype)
                throw new Error("Run AppGlueCommonInit.init() before using this class");

            this.mappingData =
                // @ts-ignore
                new ExpressionValue.mappingPrototype.constructor() as Executable;
            this.mappingData.setStorageData(Reflect.get(data, "mappingValue"));
            this.setupMappingResult();

            if (this.editContext) {
                this.mappingData.inputData = () => {
                    return this.editContext!.expression.getInputDefinition();
                };
            }

            this.setupMappingResult(this.mappingData);
        }

        if (Reflect.get(data, "mergeValue")) {
            if (!ExpressionValue.mergePrototype)
                throw new Error("Run AppGlueCommonInit.init() before using this class");

            this.stringMergeData =
                // @ts-ignore
                new ExpressionValue.mergePrototype.constructor() as RuleExecutable;
            this.stringMergeData.setStorageData(Reflect.get(data, "mergeValue"));
            if (this.editContext)
                this.stringMergeData.inputData = () => {
                    return this.editContext!.expression.getInputDefinition();
                };
        }

        this.configDescription = Reflect.get(data, "configDescription");
    }

    getVariableNames(): string[] | undefined {
        if (this.valueTypeValue === ExpressionValueType.VARIABLE) {
            return [this.variableNameValue!];
        } else if (this.valueTypeValue === ExpressionValueType.SUBEXPRESSION) {
            return this.subExpression?.getVariableNames();
        }
        return [];
    }

    private setupMappingResult(data?: Executable) {
        if (data) {
            data.executionResult = () => {
                let dataDefinition = new DataDefinition();
                dataDefinition.fields = [this.expectedTypeValue];
                return dataDefinition;
            };
        }
    }
}
